/**
 * The Task attention pulse — a decision-free timing helper.
 *
 * Plan C (`.codex/features/spatial-focus/README.md`) moved every spatial DECISION this file used
 * to own — the ghost/target precedence (`buildTaskIsolationPaintPlan`) and the camera-fit
 * precedence (`buildTaskIsolationFitDbIds`) — into the one client authority
 * (`useSpatialFocus` -> `spatialFocusArbitrator` -> `forgeSpatialFocusAdapter`). What remains is
 * the bounded, WCAG-limited, reduced-motion-aware flash loop: it chooses no colour, no target and
 * no scope, it only sequences colours it is handed.
 *
 * It stays here rather than moving because `forgeSpatialFocusAdapter.js:34-38` is its caller and a
 * relocation would be pure churn. The caller-authority gate whitelists that import for exactly
 * this reason.
 *
 * Gates: `.codex/tests/task-scoped-isolation.assert.cjs`,
 *        `.codex/tests/spatial-focus-caller-authority.assert.cjs`
 */

export const TASK_ISOLATION_NATURAL_FLASH_COLOR = '#FFFFFF';
export const TASK_ISOLATION_PULSE_LIGHTEN = 0.55;
export const TASK_ISOLATION_PULSE_INTERVAL_MS = 250;
export const TASK_ISOLATION_PULSE_DURATION_MS = 1500;

function uniqueDbIds(values) {
  return [...new Set((values || []).filter((dbId) => dbId !== null && dbId !== undefined))];
}

/**
 * Start a bounded attention pulse and return an idempotent stop function.
 * The callback interface keeps timing testable and keeps Forge calls outside
 * this pure module.
 */
export function startTaskIsolationPulse(groups, {
  setColor,
  clearColor,
  setIntervalFn = setInterval,
  clearIntervalFn = clearInterval,
  prefersReducedMotion = false,
} = {}) {
  const activeGroups = (groups || [])
    .map((group) => ({
      ...group,
      dbIds: uniqueDbIds(group?.dbIds),
    }))
    .filter((group) => group.dbIds.length > 0);

  if (
    prefersReducedMotion
    || activeGroups.length === 0
    || typeof setColor !== 'function'
    || typeof clearColor !== 'function'
  ) {
    return () => {};
  }

  let timer = null;
  let stopped = false;
  let tick = 0;
  const totalTicks = Math.floor(
    TASK_ISOLATION_PULSE_DURATION_MS / TASK_ISOLATION_PULSE_INTERVAL_MS,
  );

  const restore = () => {
    activeGroups.forEach((group) => {
      if (group.settledColor) setColor(group.dbIds, group.settledColor);
      else clearColor(group.dbIds);
    });
  };

  const stop = () => {
    if (stopped) return;
    stopped = true;
    if (timer !== null && timer !== undefined) clearIntervalFn(timer);
    timer = null;
    restore();
  };

  timer = setIntervalFn(() => {
    if (stopped) return;
    tick += 1;
    const flashing = tick % 2 === 1;
    activeGroups.forEach((group) => {
      if (flashing) setColor(group.dbIds, group.flashColor);
      else if (group.settledColor) setColor(group.dbIds, group.settledColor);
      else clearColor(group.dbIds);
    });
    if (tick >= totalTicks) stop();
  }, TASK_ISOLATION_PULSE_INTERVAL_MS);

  return stop;
}
